import 'katex/dist/katex.min.css';
import React, { useEffect, useRef, useState } from 'react';
import { BlockMath, InlineMath } from 'react-katex';

import { useTOC } from '@ui/TOCContext';

import { generateAnchor } from '@utils/generateAnchor';

type TypographyProps = {
  children: React.ReactNode;
  className?: string;
};

// Article Container Component
export const ArticleContainer: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => (
  <article
    className={`bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-sm p-4 sm:p-8 lg:px-12 w-full min-w-0 ${className}`}
  >
    {children}
  </article>
);

// Article Metadata Component
type ArticleMetaProps = {
  readingTime: string;
  author?: string;
  lastUpdated?: Date;
  title?: string;
  className?: string;
};

export const ArticleMeta: React.FC<ArticleMetaProps> = ({
  readingTime,
  author,
  lastUpdated = new Date(),
  title,
  className = '',
}) => {
  const [showTitle, setShowTitle] = useState(false);

  useEffect(() => {
    if (!title) return;

    const handleScroll = () => {
      // Look for H1 element to determine if main title is visible
      const h1Element = document.querySelector('h1');
      if (h1Element) {
        const rect = h1Element.getBoundingClientRect();
        // Show title in meta when H1 is above the viewport
        setShowTitle(rect.bottom < 0);
      }
    };

    // Initial check
    handleScroll();

    // Throttled scroll listener
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll);
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [title]);

  return (
    <div className={`mb-6 ${className}`}>
      <div className="bg-white border border-gray-200 rounded-lg p-4 w-full lg:max-w-none lg:w-auto">
        <div className="space-y-2">
          {title && (
            <div
              className={`text-sm font-medium text-gray-900 transition-all duration-300 ease-in-out overflow-hidden ${
                showTitle
                  ? 'opacity-100 max-h-20 border-b border-gray-100 pb-2 mb-3'
                  : 'opacity-0 max-h-0 pb-0 mb-0'
              }`}
            >
              {title}
            </div>
          )}
          <div className="text-xs uppercase text-gray-400">
            <strong>Reading time:</strong> {readingTime}
          </div>
          {author && (
            <div className="text-xs uppercase text-gray-400">
              <strong>Author:</strong> {author}
            </div>
          )}
          <div className="text-xs uppercase text-gray-400">
            <strong>Last updated:</strong>{' '}
            <time dateTime={lastUpdated.toISOString()}>
              {lastUpdated.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </div>
        </div>
      </div>
    </div>
  );
};

// Section Component
export const Section: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <section className={`mb-12 ${className}`}>{children}</section>;

// Heading Components
export const H1: React.FC<TypographyProps> = ({ children, className = '' }) => (
  <h1
    className={`text-4xl md:text-5xl font-bold mb-8 text-gray-900 ${className}`}
  >
    {children}
  </h1>
);

export const H2: React.FC<TypographyProps> = ({ children, className = '' }) => {
  const anchor = typeof children === 'string' ? generateAnchor(children) : '';
  const title = typeof children === 'string' ? children : '';
  const { registerHeading } = useTOC();

  useEffect(() => {
    if (title && anchor) {
      registerHeading(title, anchor, 'h2');
    }
  }, [title, anchor, registerHeading]);

  return (
    <h2
      id={anchor}
      className={`scroll-mt-24 text-3xl sm:text-4xl font-bold leading-tight tracking-tight text-gray-900 mb-8 mt-12 first:mt-0 pb-4 border-b border-gray-200 ${className}`}
    >
      <a
        href={`#${anchor}`}
        className="flex items-center hover:text-gray-700 transition-colors"
      >
        <span className="text-gray-400 mr-2 text-xl">#</span>
        {children}
      </a>
    </h2>
  );
};

export const H3: React.FC<TypographyProps> = ({ children, className = '' }) => {
  const anchor = typeof children === 'string' ? generateAnchor(children) : '';
  const title = typeof children === 'string' ? children : '';
  const { registerHeading } = useTOC();

  useEffect(() => {
    if (title && anchor) {
      registerHeading(title, anchor, 'h3');
    }
  }, [title, anchor, registerHeading]);

  return (
    <h3
      id={anchor}
      className={`scroll-mt-24 text-2xl font-bold mb-6 text-gray-900 ${className}`}
    >
      {children}
    </h3>
  );
};

export const H4: React.FC<TypographyProps> = ({ children, className = '' }) => (
  <h4 className={`text-xl font-bold text-gray-900 ${className}`}>{children}</h4>
);

export const H5: React.FC<TypographyProps> = ({ children, className = '' }) => (
  <h5 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h5>
);

// Paragraph Components
export const P: React.FC<TypographyProps> = ({ children, className = '' }) => (
  <p className={`text-lg text-gray-800 leading-relaxed ${className}`}>
    {children}
  </p>
);

export const PLead: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => (
  <p
    className={`text-lg text-gray-800 leading-relaxed font-medium ${className}`}
  >
    {children}
  </p>
);

export const PSmall: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => (
  <p className={`text-base text-gray-700 leading-relaxed ${className}`}>
    {children}
  </p>
);

// Text Emphasis Components
export const Strong: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => (
  <strong className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </strong>
);

export const Emphasis: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => (
  <span className={`text-lg text-gray-800 leading-relaxed ${className}`}>
    {children}
  </span>
);

// List Components
type ListProps = TypographyProps & {
  spacing?: 'tight' | 'normal' | 'loose';
};

export const UL: React.FC<ListProps> = ({
  children,
  spacing = 'normal',
  className = '',
}) => {
  const spacingClasses = {
    tight: 'space-y-2',
    normal: 'space-y-4',
    loose: 'space-y-6',
  };

  return (
    <ul
      className={`${spacingClasses[spacing]} list-disc ml-12 mb-6 ${className}`}
    >
      {children}
    </ul>
  );
};

export const OL: React.FC<ListProps> = ({
  children,
  spacing = 'normal',
  className = '',
}) => {
  const spacingClasses = {
    tight: 'space-y-2',
    normal: 'space-y-3',
    loose: 'space-y-6',
  };

  return (
    <ol
      className={`${spacingClasses[spacing]} list-decimal ml-12 mb-6 ${className}`}
    >
      {children}
    </ol>
  );
};

export const LI: React.FC<TypographyProps> = ({ children, className = '' }) => (
  <li className={`leading-7 text-gray-700 ${className}`}>{children}</li>
);

// Definition List Components
export const DefinitionList: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <ul className={`space-y-4 ${className}`}>{children}</ul>;

type DefinitionItemProps = {
  term: React.ReactNode;
  definition: React.ReactNode;
  className?: string;
};

export const DefinitionItem: React.FC<DefinitionItemProps> = ({
  term,
  definition,
  className = '',
}) => (
  <li className={className}>
    <Strong>{term}:</Strong>
    <Emphasis> {definition}</Emphasis>
  </li>
);

// Special Components
export const CodeBlock = ({ children, className = '' }: TypographyProps) => (
  <div className="my-4 overflow-x-auto -mx-4 sm:mx-0">
    <div className="inline-block min-w-full px-4 sm:px-0">
      <div
        className={`p-4 bg-gray-100 border-l-4 border-gray-400 ${className}`}
      >
        <div className="font-mono text-md text-gray-900 whitespace-pre-wrap break-words">
          {children}
        </div>
      </div>
    </div>
  </div>
);

export const Quote: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => (
  <blockquote
    className={`border-l-4 border-blue-500 pl-4 my-6 italic text-lg text-gray-700 ${className}`}
  >
    {children}
  </blockquote>
);

// Math Components
type MathProps = {
  children: string;
  className?: string;
};

export const Math: React.FC<MathProps> = ({ children, className = '' }) => (
  <span className={className}>
    <InlineMath math={children} />
  </span>
);

export const MathBlock = ({ children, className = '' }: MathProps) => (
  <div className="my-6 overflow-x-auto -mx-4 sm:mx-0">
    <div className="inline-block min-w-full px-4 sm:px-0">
      <div className={`text-center text-sm sm:text-base ${className}`}>
        <BlockMath math={children} />
      </div>
    </div>
  </div>
);

export const Formula: React.FC<MathProps & { title?: string }> = ({
  children,
  title,
  className = '',
}) => (
  <div className="my-6 overflow-x-auto -mx-4 sm:mx-0">
    <div className="inline-block min-w-full px-4 sm:px-0">
      <div
        className={`p-4 bg-gray-50 border border-gray-200 rounded-none sm:rounded-lg ${className}`}
      >
        {title && (
          <div className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
            {title}
          </div>
        )}
        <div className="text-center overflow-x-auto text-sm sm:text-base">
          <BlockMath math={children} />
        </div>
      </div>
    </div>
  </div>
);

// Link Component
type LinkProps = TypographyProps & {
  href: string;
  underline?: boolean;
  target?: string;
};

export const Link: React.FC<LinkProps> = ({
  children,
  href,
  target,
  underline = false,
  className = '',
}) => (
  <a
    href={href}
    target={target}
    className={`text-blue-600 hover:text-blue-600 ${underline ? 'underline hover:no-underline' : 'hover:underline'} transition-colors duration-200 ${className}`}
  >
    {children}
  </a>
);

// Content Spacing Components
export const ContentBlock: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <div className={`mb-8 ${className}`}>{children}</div>;

export const SubSection: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <div className={`space-y-6 ${className}`}>{children}</div>;

// Introduction Block
export const IntroductionBlock: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <div className={`mb-12 ${className}`}>{children}</div>;

// Step-by-step guide components
export const StepList: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <div className={`space-y-4 ${className}`}>{children}</div>;

type StepItemProps = {
  title: string;
  children: React.ReactNode;
  className?: string;
};

export const StepItem: React.FC<StepItemProps> = ({
  title,
  children,
  className = '',
}) => (
  <div className={className}>
    <H3 className="mb-4">{title}</H3>
    {children}
  </div>
);

// Table Components
type TableProps = {
  children: React.ReactNode;
  className?: string;
};

export const Table: React.FC<TableProps> = ({ children, className = '' }) => (
  <div className="overflow-x-auto mb-6 -mx-4 sm:mx-0">
    <div className="inline-block min-w-full px-4 sm:px-0">
      <table
        className={`w-full border-collapse bg-white rounded-none sm:rounded-lg overflow-hidden shadow-sm ${className}`}
      >
        {children}
      </table>
    </div>
  </div>
);

export const THead: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <thead className={className}>{children}</thead>;

export const TBody: React.FC<TypographyProps> = ({
  children,
  className = '',
}) => <tbody className={className}>{children}</tbody>;

export const TR: React.FC<TypographyProps> = ({ children, className = '' }) => (
  <tr className={className}>{children}</tr>
);

type TCellProps = TypographyProps & {
  align?: 'left' | 'center' | 'right';
};

export const TH: React.FC<TCellProps> = ({
  children,
  align = 'left',
  className = '',
}) => {
  const alignClass =
    align === 'center'
      ? 'text-center'
      : align === 'right'
        ? 'text-right'
        : 'text-left';

  return (
    <th
      className={`${alignClass} py-3 sm:py-4 px-3 sm:px-4 font-semibold text-gray-900 text-sm sm:text-base bg-gray-50 border-b-2 border-gray-200 ${className}`}
    >
      {children}
    </th>
  );
};

export const TD: React.FC<TCellProps> = ({
  children,
  align = 'left',
  className = '',
}) => {
  const alignClass =
    align === 'center'
      ? 'text-center'
      : align === 'right'
        ? 'text-right'
        : 'text-left';

  return (
    <td
      className={`${alignClass} py-3 sm:py-4 px-3 sm:px-4 text-gray-700 text-sm sm:text-base border-b border-gray-100 last:border-b-0 ${className}`}
    >
      {children}
    </td>
  );
};

// Table of Contents Components

export const TableOfContents: React.FC<{ className?: string }> = ({
  className = '',
}) => {
  const { items, activeHeading } = useTOC();
  const navRef = useRef<HTMLElement>(null);

  // Auto-scroll active item into view
  useEffect(() => {
    if (!activeHeading || !navRef.current || items.length === 0) return;

    const activeElement = navRef.current.querySelector(
      `[data-anchor="${activeHeading}"]`
    );
    if (activeElement) {
      activeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [activeHeading, items.length]);

  if (items.length === 0) {
    return null; // Don't render if no headings are registered
  }

  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg overflow-hidden ${className}`}
    >
      {/* Fixed Header */}
      <div className="p-4 pb-3 border-b border-gray-100">
        <h4 className="text-sm text-gray-400 uppercase tracking-wide">
          Table of Contents
        </h4>
      </div>

      {/* Scrollable Navigation */}
      <nav
        ref={navRef}
        className="max-h-[calc(100vh-22rem)] overflow-y-auto p-4 pt-3 space-y-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400"
      >
        {items.map((item, index) => {
          const isActive = activeHeading === item.anchor;
          const isH2 = item.level === 'h2';

          return (
            <a
              key={index}
              href={`#${item.anchor}`}
              data-anchor={item.anchor}
              className={`block text-sm transition-all duration-200 ${
                isActive
                  ? isH2
                    ? 'font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded border-l-4 border-blue-600'
                    : 'font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded pl-5 border-l-4 border-blue-400'
                  : isH2
                    ? 'font-medium text-gray-900 py-1 hover:text-blue-600'
                    : 'text-gray-600 pl-3 py-0.5 border-l-2 border-gray-100 hover:border-blue-200 hover:text-blue-600'
              }`}
            >
              {item.title}
            </a>
          );
        })}
      </nav>
    </div>
  );
};
